// src/Pages/EventCalendar.jsx
import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Select,
  FormControl,
  Chip,
  Container,
  Fade,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Tooltip,
  Popover,
} from "@mui/material";
import { EventAvailable, MoreHoriz } from "@mui/icons-material";
import apiEndpoints from "../../apiconfig";
import { useTheme, useMediaQuery } from "@mui/material";

/* ---------------- CONSTANTS ---------------- */
const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const EventCalendar = () => {
  /* ---------------- STATE ---------------- */
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [current, setCurrent] = useState(null);
  const role = localStorage.getItem("role"); // "admin" | "staff" | "teacher"

  const isAdmin = role === "admin";

  const year = current?.year;
  const monthIndex = current?.monthIndex;
  const token = localStorage.getItem("token");
  const [openDialog, setOpenDialog] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    start_date: "",
    end_date: "",
    color: "#1976D2",
  });
  
  // State for mobile popover
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverDate, setPopoverDate] = useState(null);
  const [popoverEvents, setPopoverEvents] = useState([]);

  /* ---------------- BUILD CALENDAR GRID ---------------- */
  const { cells } = useMemo(() => {
    if (year === null || monthIndex === null) return { cells: [] };

    const firstDayJs = new Date(year, monthIndex, 1).getDay();
    const firstDayIndex = (firstDayJs + 6) % 7; // Monday start
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

    const temp = [];
    const totalCells = firstDayIndex + daysInMonth;

    for (let i = 0; i < totalCells; i++) {
      temp.push(i < firstDayIndex ? null : i - firstDayIndex + 1);
    }

    return { cells: temp };
  }, [year, monthIndex]);

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    fetch(`${apiEndpoints.calendar}?action=branches`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.status) {
          setBranches(res.data.branches);
          setSelectedBranch(res.data.branches[0]); // default
        }
      });
  }, []);

  /* ---------------- FETCH MONTHS ---------------- */
  useEffect(() => {
    fetch(`${apiEndpoints.calendar}?action=months`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.status) {
          setMonths(res.data.months);

          if (res.data.months.length) {
            const now = new Date();
            const currentYear = now.getFullYear();
            const currentMonth = now.getMonth() + 1;

            const currentMonthObj = res.data.months.find(
              (m) => m.year === currentYear && m.month === currentMonth,
            );

            setSelectedMonth(currentMonthObj || res.data.months[0]);
          } else {
            // 🔥 fallback to current month
            const now = new Date();
            const fallback = {
              year: now.getFullYear(),
              month: now.getMonth() + 1,
              label: now.toLocaleString("default", {
                month: "long",
                year: "numeric",
              }),
            };

            setMonths([fallback]);
            setSelectedMonth(fallback);
          }
        }
      });
  }, []);

  /* ---------------- FETCH EVENTS FOR MONTH ---------------- */
  useEffect(() => {
    if (!selectedMonth || !selectedBranch) return;

    fetch(
      `${apiEndpoints.calendar}?action=month&branch_guid=${selectedBranch.guid}&year=${selectedMonth.year}&month=${selectedMonth.month}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    )
      .then((res) => res.json())
      .then((res) => {
        setCurrent({
          year: selectedMonth.year,
          monthIndex: selectedMonth.month - 1,
          days: res.status ? res.data.events || {} : {},
        });
      });
  }, [selectedMonth, selectedBranch]);

  const openEventDialog = ({ date, event = null }) => {
    if (event) {
      // EDIT MODE
      setEditEvent(event);
      setForm(event);
    } else {
      // CREATE MODE (date click)
      const d = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(
        date,
      ).padStart(2, "0")}`;
      setEditEvent(null);
      setForm({
        title: "",
        description: "",
        start_date: d,
        end_date: d,
        color: "#1976D2",
      });
    }
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setEditEvent(null);
  };

  const refreshCalendar = () => {
    fetch(
      `${apiEndpoints.calendar}?action=month&branch_guid=${selectedBranch.guid}&year=${selectedMonth.year}&month=${selectedMonth.month}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
      .then((res) => res.json())
      .then((res) => {
        setCurrent({
          year: selectedMonth.year,
          monthIndex: selectedMonth.month - 1,
          days: res.status ? res.data.events || {} : {},
        });
      });
  };

  const handleSave = () => {
    const action = editEvent ? "update" : "add";

    const payload = {
      ...form,
      branch_guid: selectedBranch.guid,
      id: editEvent?.id,
    };

    fetch(`${apiEndpoints.calendar}?action=${action}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams(payload),
    })
      .then((res) => res.json())
      .then(() => {
        closeDialog();
        refreshCalendar();
      });
  };

  const handleDelete = () => {
    fetch(`${apiEndpoints.calendar}?action=delete&id=${editEvent.id}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then(() => {
        closeDialog();
        refreshCalendar();
      });
  };

  // Mobile: Open popover to show all events for a date
  const openEventPopover = (date, events, anchorEl) => {
    if (!isMobile || events.length === 0) return;
    setPopoverDate(date);
    setPopoverEvents(events);
    setPopoverAnchor(anchorEl);
  };

  // Mobile: Close popover
  const closeEventPopover = () => {
    setPopoverAnchor(null);
    setPopoverDate(null);
    setPopoverEvents([]);
  };

  // Handle date click - different behavior for mobile vs desktop
  const handleDateClick = (date, dayEvents, event) => {
    if (!isAdmin) return;
    
    if (isMobile) {
      // On mobile: If there are events, show them in popover
      if (dayEvents.length > 0) {
        openEventPopover(date, dayEvents, event.currentTarget);
      } else {
        // No events - open dialog to add new event
        openEventDialog({ date });
      }
    } else {
      // On desktop: Open dialog to add new event
      openEventDialog({ date });
    }
  };

  // Handle event click on mobile (from popover)
  const handleMobileEventClick = (ev) => {
    closeEventPopover();
    openEventDialog({ event: ev });
  };

  /* ---------------- UI ---------------- */
  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        background: "rgba(255, 255, 255, 0.25)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.4)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `
             radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
             radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)
           `,
          pointerEvents: "none",
        },
      }}
    >
      {!current && (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Loading calendar...
          </Typography>
        </Box>
      )}

      {current && (
        <>
          {/* HEADER */}
          <Fade in timeout={700}>
            <Box
              sx={{
                py: 4,
                px: { xs: 2, md: 4 },
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                gap: { xs: 2, sm: 3 },
              }}
            >
              {/* LEFT: TITLE */}
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#333",
                    mb: 0.5,
                    fontSize: { xs: "1.7rem", md: "2.1rem" },
                  }}
                >
                  School Event Calendar
                </Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  View and manage school events by date.
                </Typography>
              </Box>

              {/* RIGHT: DROPDOWNS IN ONE BOX */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {/* Branch Dropdown */}
                <FormControl sx={{ minWidth: { xs: "100%", sm: 180 } }}>
                  <Select
                    value={selectedBranch?.guid || ""}
                    onChange={(e) =>
                      setSelectedBranch(
                        branches.find((b) => b.guid === e.target.value),
                      )
                    }
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: {
                        sx: {
                          borderRadius: "14px",
                          maxHeight: 280,
                          zIndex: 1500,
                        },
                      },
                    }}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.95)",
                      borderRadius: "14px",
                      px: 1.5,
                    }}
                    disabled={!isAdmin}
                  >
                    {branches.map((b) => (
                      <MenuItem key={b.guid} value={b.guid}>
                        {b.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Month Dropdown */}
                <FormControl sx={{ minWidth: { xs: "100%", sm: 180 } }}>
                  <Select
                    value={selectedMonth?.label || ""}
                    onChange={(e) => {
                      const m = months.find((x) => x.label === e.target.value);
                      setSelectedMonth(m);
                    }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: {
                        sx: {
                          borderRadius: "14px",
                          maxHeight: 320,
                          zIndex: 1500,
                        },
                      },
                    }}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.95)",
                      borderRadius: "14px",
                      px: 1.5,
                    }}
                  >
                    {months.map((m) => (
                      <MenuItem key={m.label} value={m.label}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Fade>

          {/* CALENDAR */}
          <Fade in timeout={1100}>
            <Box sx={{ px: { xs: 2, md: 4 }, pb: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "22px",
                  background: "rgba(255,255,255,0.7)",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(255,255,255,0.5)",
                  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12)",
                }}
              >
                {/* Calendar Header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2.5,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <EventAvailable /> Calendar View
                  </Typography>
                  <Chip
                    size="small"
                    label="Event"
                    sx={{
                      bgcolor: alpha("#1976D2", 0.12),
                      color: "#1565C0",
                      fontSize: { xs: "0.45rem", sm: "0.75rem" },
                      height: 22,
                    }}
                  />
                </Box>

                {/* Weekdays */}
                <Grid
                  container
                  columns={isMobile ? 7 : { sm: 4, md: 7 }}
                  sx={{ mb: 1 }}
                >
                  {weekdayLabels.map((w) => (
                    <Grid item xs={1} key={w}>
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 0.5,
                          fontSize: { xs: 11, sm: 13 },
                          fontWeight: 600,
                          color: "#9e9e9e",
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {w}
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Days */}
                <Grid
                  container
                  columns={isMobile ? 7 : { sm: 4, md: 7 }}
                  spacing={isMobile ? 0.8 : { sm: 1.2 }}
                >
                  {cells.map((date, idx) => {
                    if (!date) {
                      return (
                        <Grid item xs={1} key={`empty-${idx}`}>
                          <Box
                            sx={{
                              borderRadius: "14px",
                              minHeight: 40,
                              bgcolor: "rgba(248,248,248,0.6)",
                              border: "1px dashed rgba(0,0,0,0.04)",
                            }}
                          />
                        </Grid>
                      );
                    }

                    const dayEvents = current.days[date] || [];
                    const today = new Date();
                    const isToday = 
                      date === today.getDate() &&
                      monthIndex === today.getMonth() &&
                      year === today.getFullYear();

                    return (
                      <Grid item xs={1} key={idx}>
                      {/* Desktop: Tooltip for hover */}
                      {!isMobile && dayEvents.length > 0 ? (
                        <Tooltip
                        title={
                          <Box>
                          {dayEvents.map((ev) => (
                            <Box key={ev.id} sx={{ mb: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {ev.title}
                            </Typography>
                            {ev.description && (
                              <Typography variant="caption">
                              {ev.description}
                              </Typography>
                            )}
                            </Box>
                          ))}
                          </Box>
                        }
                        arrow
                        placement="top"
                        >
                        <Box
                          sx={{
                          borderRadius: isMobile ? "18px" : "14px",
                          height: isMobile ? 96 : { sm: 100, md: 120 },
                          p: isMobile ? 0.6 : { sm: 1 },
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          bgcolor: "rgba(255,255,255,0.95)",
                          border: "1px solid rgba(0,0,0,0.06)",
                          transition: "all 0.15s ease",
                          cursor: isAdmin ? "pointer" : "default",
                          "&:hover": !isMobile && {
                            transform: "translateY(-2px)",
                            boxShadow: "0 8px 18px rgba(15,23,42,0.18)",
                          },
                          position: "relative",
                          }}
                          onClick={(e) => handleDateClick(date, dayEvents, e)}
                        >
                          <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: 13, sm: 16 },
                            color: isToday ? "#1976D2" : "#616161",
                          }}
                          >
                          {date}
                          </Typography>

                          {/* Desktop: Show events */}
                          {!isMobile && (
                          <Box
                            sx={{
                            mt: 0.5,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                            overflowY: "auto",
                            flexGrow: 1,
                            pr: 0.5,
                            width: "100%",
                            }}
                          >
                            {dayEvents.map((ev) => (
                            <Chip
                              key={ev.id}
                              label={ev.title}
                              size="small"
                              onClick={(e) => {
                              e.stopPropagation();
                              if (!isAdmin) return;
                              openEventDialog({ event: ev });
                              }}
                              sx={{
                              bgcolor: alpha("#1976D2", 0.12),
                              color: "#1565C0",
                              fontWeight: 600,
                              maxWidth: "100%",
                              cursor: "pointer",
                              }}
                            />
                            ))}
                          </Box>
                          )}
                        </Box>
                        </Tooltip>
                      ) : (
                        // Mobile or desktop without events
                        <Box
                        sx={{
                          borderRadius: isMobile ? "18px" : "14px",
                          height: isMobile ? 40 : { sm: 100, md: 120 },
                          p: isMobile ? 0.6 : { sm: 1 },
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          bgcolor: isToday ? alpha("#1976D2", 0.1) : "rgba(255,255,255,0.95)",
                          border: isToday 
                          ? `2px solid ${alpha("#1976D2", 0.3)}`
                          : "1px solid rgba(0,0,0,0.06)",
                          transition: "all 0.15s ease",
                          cursor: isAdmin ? "pointer" : "default",
                          "&:hover": !isMobile && {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 18px rgba(15,23,42,0.18)",
                          },
                          position: "relative",
                        }}
                        onClick={(e) => handleDateClick(date, dayEvents, e)}
                        >
                        <Typography
                          sx={{
                          fontWeight: 700,
                          fontSize: { xs: 13, sm: 16 },
                          color: isToday ? "#1976D2" : "#616161",
                          }}
                        >
                          {date}
                        </Typography>

                        {/* Mobile: Event indicator (single dot) */}
                        {isMobile && dayEvents.length > 0 && (
                          <Box
                          sx={{
                            position: "absolute",
                            bottom: 5, // Keep the dot at the bottom
                            left: "50%",
                            transform: "translate(-50%, 0)",
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: "#1976D2",
                          }}
                          />
                        )}
                        </Box>
                      )}
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Mobile Instructions */}
                {isMobile && (
                  <Box sx={{ mt: 3, textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      {isAdmin 
                        ? "Tap date to add events • Tap dots to view/edit events"
                        : "Tap dots to view events"}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          </Fade>
        </>
      )}

      {/* Mobile Popover for Events */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={closeEventPopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxWidth: 300,
            maxHeight: 400,
            p: 2,
          }
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Events on {popoverDate} {selectedMonth?.label?.split(' ')[0]}
        </Typography>
        
        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
          {popoverEvents.map((ev) => (
            <Box
              key={ev.id}
              sx={{
                p: 1.5,
                mb: 1,
                borderRadius: "8px",
                bgcolor: alpha("#1976D2", 0.08),
                borderLeft: `3px solid ${alpha("#1976D2", 0.5)}`,
                cursor: isAdmin ? "pointer" : "default",
                "&:hover": isAdmin && {
                  bgcolor: alpha("#1976D2", 0.12),
                },
              }}
              onClick={() => isAdmin && handleMobileEventClick(ev)}
            >
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                {ev.title}
              </Typography>
              {ev.description && (
                <Typography variant="caption" color="text.secondary">
                  {ev.description}
                </Typography>
              )}
              <Typography variant="caption" display="block" color="#666" mt={0.5}>
                {ev.start_date} {ev.end_date !== ev.start_date ? `- ${ev.end_date}` : ''}
              </Typography>
            </Box>
          ))}
        </Box>
        
        {isAdmin && (
          <Button
            fullWidth
            variant="contained"
            size="small"
            onClick={() => {
              closeEventPopover();
              openEventDialog({ date: popoverDate });
            }}
            sx={{ mt: 2 }}
          >
            Add New Event
          </Button>
        )}
      </Popover>

      {/* Event Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={closeDialog} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: isMobile ? "1.2rem" : "inherit" }}>
          {editEvent ? "Edit Event" : "Create Event"}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              disabled={!isAdmin}
              fullWidth
              required
              size={isMobile ? "small" : "medium"}
            />

            <TextField
              label="Description"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              disabled={!isAdmin}
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              size={isMobile ? "small" : "medium"}
            />

            <TextField
              label="Start Date"
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              disabled={!isAdmin}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size={isMobile ? "small" : "medium"}
            />

            <TextField
              label="End Date"
              type="date"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              disabled={!isAdmin}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size={isMobile ? "small" : "medium"}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ 
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 1 : 0,
          p: isMobile ? 2 : 1,
        }}>
          <Box sx={{ 
            display: "flex", 
            gap: 1,
            width: isMobile ? "100%" : "auto",
            order: isMobile ? 2 : 1,
          }}>
            {isAdmin && editEvent && (
              <Button 
                color="error" 
                onClick={handleDelete}
                size={isMobile ? "small" : "medium"}
                variant="outlined"
                fullWidth={isMobile}
              >
                Delete
              </Button>
            )}

            <Button 
              onClick={closeDialog}
              size={isMobile ? "small" : "medium"}
              variant="outlined"
              fullWidth={isMobile && !(isAdmin && editEvent)}
            >
              Close
            </Button>
          </Box>

          {isAdmin && (
            <Button 
              variant="contained" 
              onClick={handleSave}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
              sx={{
                order: isMobile ? 1 : 2,
                mb: isMobile ? 1 : 0,
              }}
            >
              {editEvent ? "Update" : "Create"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventCalendar;